import { Button, Select, SelectOption, Sentiment, TextArea } from "@clockwise/design-system";
import { Size as ButtonSize } from "@clockwise/design-system/src/components/Button";
import { Size as SelectSize } from "@clockwise/design-system/src/constants/types";
import { CwIdProps } from "@clockwise/design-system/src/types/cw-id";
import {
  EditRounded as EditIcon,
  DeleteRounded as RemoveIcon,
  SvgIconComponent,
} from "@material-ui/icons";
import { isEmpty, noop, trim } from "lodash";
import React, {
  ButtonHTMLAttributes,
  ComponentProps,
  FocusEvent,
  KeyboardEvent,
  useRef,
} from "react";
import { useBoolean, useEffectOnce } from "usehooks-ts";

export interface Props extends CwIdProps {
  buttonProps?: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "disabled" | "text">;
  placeholder: string;
  disabled?: boolean;
  inputProps?: Omit<
    ComponentProps<typeof TextArea>,
    "className" | "disabled" | "value" | "placeholder"
  >;
  customActions?: React.ReactNode[];
  onChange: (value: string) => void;
  editIcon?: SvgIconComponent;
  selectProps?: Omit<ComponentProps<typeof Select>, "onChange">;
  sentiment?: Sentiment;
  value: string;
}

/**
 * An input component that combines a button, select, and input into a single UX.
 *  - button is shown when no value is present
 *  - select is shown for the current value and to allow for editing or removing options
 *  - input is shown when the user is activly editing the value
 *
 *  @see for useage details see InputButton.stories.tsx and its interactions
 *
 * @example <InputButton placeholder="Select a value" onChange={setValue} value={value} />
 *
 */
export const InputButton = (props: Props) => {
  const { value: isOpen, setTrue: setOpen, setFalse: setClose } = useBoolean(false);

  const handleChange = (v?: string) => {
    setClose();
    props.onChange?.(v || "");
  };

  const handleRemove = () => handleChange("");

  if (isOpen && !props.disabled) {
    return (
      <div cw-id={props["cw-id"]}>
        <InputButton.Input onChange={handleChange} placeholder={props.placeholder}>
          {props.value}
        </InputButton.Input>
      </div>
    );
  } else if (props.value && !isEmpty(props.value)) {
    return (
      <div cw-id={props["cw-id"]} className="cw-py-1">
        <InputButton.Select
          {...props.selectProps}
          current={props.value}
          disabled={props.disabled || false}
          editIcon={props.editIcon}
          onEdit={setOpen}
          onRemove={handleRemove}
          size="small"
          customActions={props.customActions}
        />
      </div>
    );
  } else {
    return (
      <div cw-id={props["cw-id"]} className="cw-py-1">
        <InputButton.Button
          {...props.buttonProps}
          disabled={props.disabled || false}
          onClick={setOpen}
          value={props.value}
          size="xsmall"
          placeholder={props.placeholder}
        />
      </div>
    );
  }
};

interface InputButton_ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean;
  onClick?: () => void;
  value?: string;
  placeholder: string;
  customAction?: { action: () => unknown; title: string } | null;
  size?: ButtonSize;
}

const InputButton_Button = ({
  onClick,
  disabled,
  value,
  placeholder,
  size = "xsmall",
  ...buttonProps
}: InputButton_ButtonProps) => {
  return (
    <Button {...buttonProps} disabled={disabled} onClick={onClick} size={size} variant="text">
      {value ? value : placeholder}
    </Button>
  );
};

InputButton.Button = InputButton_Button;

interface InputButton_SelectProps
  extends Omit<ComponentProps<typeof Select>, "onChange" | "value"> {
  current: string;
  disabled: boolean;
  editIcon?: SvgIconComponent;
  customActions?: React.ReactNode[];
  onEdit: () => void;
  onRemove: () => void;
  size?: SelectSize;
}

const InputButton_Select = ({
  children,
  current,
  disabled,
  editIcon,
  onEdit,
  onRemove,
  size = "small",
  customActions,
  ...selectProps
}: InputButton_SelectProps) => {
  const value = "" as const; // DEV NOTE: always keep select in "unselected" state

  return (
    <Select
      {...selectProps}
      disabled={disabled}
      onChange={noop}
      size={size}
      value={value}
      variant="inline"
    >
      <SelectOption value="">{current}</SelectOption>
      {customActions && customActions.length ? customActions : null}
      <SelectOption
        value={onEdit}
        icon={editIcon || EditIcon}
        iconProps={{ className: "cw-text-muted" }}
      >
        Edit
      </SelectOption>
      <SelectOption
        value={onRemove}
        icon={RemoveIcon}
        iconProps={{ className: "cw-text-destructive" }}
      >
        Remove
      </SelectOption>
    </Select>
  );
};
InputButton.Select = InputButton_Select;

interface InputButton_InputProps
  extends Omit<ComponentProps<typeof TextArea>, "className" | "onChange"> {
  onChange: (value: string) => void;
  placeholder: string;
}

const InputButton_Input = ({
  children,
  onChange,
  placeholder,
  ...inputProps
}: InputButton_InputProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffectOnce(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  });

  const handleChange = (changeValue: string) => onChange(trim(changeValue));
  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => handleChange(e.target.value);
  const handleKeyDown = onEnterKeyDown((e: KeyboardEvent<HTMLTextAreaElement>) =>
    handleChange(e.currentTarget.value),
  );

  return (
    <TextArea
      {...inputProps}
      fieldSize="small"
      fullWidth
      inline
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      ref={inputRef}
    >
      {children}
    </TextArea>
  );
};
InputButton.Input = InputButton_Input;

// utility
const onEnterKeyDown = (callback: (e: KeyboardEvent<HTMLTextAreaElement>) => void) => (
  e: KeyboardEvent<HTMLTextAreaElement>,
) => {
  if (e.key === "Enter") {
    callback(e);
  }
};
